import type { LeadPayload, Paginated, Property, PropertyQuery } from "@/types/property";

export interface StoreStats {
  totalListings: number;
  totalCommunities: number;
  totalAgents: number;
  totalSaleValue: number;
  byType: Record<"sale" | "rent" | "off-plan", number>;
}

export interface CommunitySummary {
  name: string;
  slug: string;
  count: number;
  minPrice: number;
  coverImage?: string;
  listingTypes: Property["listingType"][];
  latestListedAt: string;
}

/** Tiny, dependency-free fetch helper for the Hive Homes API. */
async function http<T>(input: string, init?: RequestInit): Promise<T> {
  const res = await fetch(input, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`API ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<T>;
}

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const api = {
  listProperties: (q: PropertyQuery = {}): Promise<Paginated<Property>> =>
    http(`/api/properties${qs(q as Record<string, string | number | boolean | undefined>)}`),

  getProperty: (id: string): Promise<Property> => http(`/api/properties/${encodeURIComponent(id)}`),

  createLead: (payload: LeadPayload): Promise<{ ok: true; id: string | number; source: string }> =>
    http(`/api/leads`, { method: "POST", body: JSON.stringify(payload) }),

  listCommunities: (): Promise<{ communities: CommunitySummary[] }> => http(`/api/communities`),

  getStats: (): Promise<StoreStats> => http(`/api/stats`),
};
